/**
 * Which team questions a person's roles care about (roadmap R-5; questions PRD §4.4:
 * "a person with several roles sees the team questions for the contexts those roles
 * put them in, using the existing featured roles setting").
 *
 * Built on composeHomeDashboard, which already turns assigned roles plus featured
 * roles into the home widgets each role cares about (the retired page of widgets
 * used it the same way). Here each widget is read as interest in a question.
 * "Where things stand" is everyone's and is not decided here.
 */
import { composeHomeDashboard } from '../../../lib/composeHomeDashboard';
import type { HomeWidgetId } from '../../../types/prioritization/homeDashboard';

export type TeamQuestion = 'ball' | 'sinceReview' | 'decided';

const WIDGETS_FOR: Record<TeamQuestion, readonly HomeWidgetId[]> = {
  // Who holds the ball / what changed since the last review: the people who pass,
  // hold or decide on the ball, and the people who run cycles and tracks.
  ball: ['ball_position', 'proposals_awaiting_review', 'active_cycles', 'active_tracks', 'cycle_readiness'],
  sinceReview: ['ball_position', 'proposals_awaiting_review', 'active_cycles', 'active_tracks', 'cycle_readiness'],
  // What we decided: the people who decide, approve, publish or keep the plan.
  decided: ['proposals_awaiting_review', 'recent_publications', 'plans_of_record', 'objectives_progress', 'group_decisions'],
};

export interface TeamQuestionInput {
  assignedRoleNames: string[];
  featuredJobRoleSlugs?: string[];
  isAdmin: boolean;
}

export function teamQuestionsFor(input: TeamQuestionInput): ReadonlySet<TeamQuestion> {
  const { widgetIds } = composeHomeDashboard({
    assignedRoleNames: input.assignedRoleNames,
    featuredJobRoleSlugs: input.featuredJobRoleSlugs,
    useAdminShellDefault: input.isAdmin,
  });
  const have = new Set<HomeWidgetId>(widgetIds);
  const out = new Set<TeamQuestion>();
  for (const q of Object.keys(WIDGETS_FOR) as TeamQuestion[]) {
    if (WIDGETS_FOR[q].some((w) => have.has(w))) out.add(q);
  }
  return out;
}
